import { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'motion/react';

interface BalanceCountUpProps {
  value: bigint | number;
  format: (n: bigint | number) => string;
}

/**
 * Animates a money value change with a brief flash (~600ms).
 *
 * Uses motion's useMotionValue + animate() for a smooth count-up when the
 * value changes. Respects prefers-reduced-motion automatically (motion/react
 * honours it natively — no JS check needed).
 */
export function BalanceCountUp({ value, format }: BalanceCountUpProps) {
  const mv = useMotionValue(Number(value));
  const [flash, setFlash] = useState(false);
  const display = useTransform(mv, (latest) => format(BigInt(Math.round(latest))));

  useEffect(() => {
    const prev = mv.get();
    const next = Number(value);
    if (prev !== next) {
      setFlash(true);
      const controls = animate(mv, next, { duration: 0.6, ease: 'easeOut' });
      const timer = setTimeout(() => setFlash(false), 600);
      return () => {
        controls.stop();
        clearTimeout(timer);
      };
    }
  }, [value, mv]);

  return (
    <motion.span style={{ color: flash ? 'var(--success)' : undefined }}>
      <motion.span>{display}</motion.span>
    </motion.span>
  );
}
